import { api } from '@/lib/api';
import type {
  CreateQuickMessageRequest,
  QuickMessageQuery,
  QuickMessageResponse,
  UpdateQuickMessageRequest,
} from '@troia-v3/types';

export interface QuickMessagesListResponse {
  items: QuickMessageResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Quick Messages Service — CRUD + usage tracking.
 * Routes are auth-only on the backend; company-scope mutations are authorized
 * server-side (403 if the user lacks the `quick-messages` permission).
 */
export const quickMessagesService = {
  async getAll(query?: QuickMessageQuery): Promise<QuickMessagesListResponse> {
    const response = await api.get('/quick-messages', { params: query });
    return response.data.data;
  },

  async getById(id: string): Promise<QuickMessageResponse> {
    const response = await api.get(`/quick-messages/${id}`);
    return response.data.data;
  },

  async create(data: CreateQuickMessageRequest): Promise<QuickMessageResponse> {
    const response = await api.post('/quick-messages', data);
    return response.data.data;
  },

  async update(id: string, data: UpdateQuickMessageRequest): Promise<QuickMessageResponse> {
    const response = await api.put(`/quick-messages/${id}`, data);
    return response.data.data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/quick-messages/${id}`);
  },

  /** Registers usage when a quick message is inserted into the composer. */
  async registerUsage(id: string): Promise<void> {
    await api.post(`/quick-messages/${id}/use`);
  },
};
